import React, { useEffect, useRef, useState } from "react";
import { Smartphone, X, Star, Award } from "lucide-react";
import { isNativeApp } from "../native";

/* ─────────────────────────────────────────────────────────────────────────
 *  <InstallAppBanner /> — mobile-only bottom sheet that prompts users to
 *  install SkillConnect as a PWA (matching the AmbitionBox-style pattern).
 *
 *  Behaviour
 *  ─────────
 *  • Listens for the browser's `beforeinstallprompt` (Chrome/Edge/Samsung
 *    Internet on Android) and only renders when the prompt is available.
 *  • Hidden on desktop (≥ md breakpoint).
 *  • Hidden when already installed (display-mode: standalone).
 *  • Remembers a dismissal in localStorage for 7 days.
 *  • "Continue in browser" → soft dismiss only this session.
 *  • iOS Safari has no `beforeinstallprompt` — we still render an info card
 *    instructing the iOS user to use Share → Add to Home Screen.
 * ──────────────────────────────────────────────────────────────────────── */

const DISMISS_KEY  = "sc.install.dismissed_at";
const DISMISS_TTL  = 7 * 24 * 60 * 60 * 1000;          // 7 days

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches
    || window.navigator.standalone === true
  );
}

function isIos() {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

function recentlyDismissed() {
  try {
    const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return ts && Date.now() - ts < DISMISS_TTL;
  } catch { return false; }
}

export default function InstallAppBanner() {
  const promptRef = useRef(null);
  const [show, setShow]   = useState(false);
  const [iosHint, setIos] = useState(false);

  useEffect(() => {
    if (isNativeApp()) return;           // already inside the wrapper
    if (isStandalone() || recentlyDismissed()) return;
    if (isIos()) { setIos(true); setShow(true); return; }

    const onPrompt = (e) => {
      e.preventDefault();
      promptRef.current = e;
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const install = async () => {
    const evt = promptRef.current;
    if (!evt) { setShow(false); return; }
    evt.prompt();
    try {
      await evt.userChoice;
    } finally {
      promptRef.current = null;
      setShow(false);
    }
  };

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* quota */ }
    setShow(false);
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] md:hidden p-3 pointer-events-none"
      data-testid="install-app-banner"
      role="dialog"
      aria-label="Install SkillConnect app"
    >
      <div
        className="relative mx-auto max-w-md bg-white rounded-2xl shadow-2xl border border-rule overflow-hidden pointer-events-auto"
        style={{ animation: "slideInFromBottom 280ms ease-out" }}
      >
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          data-testid="install-banner-close"
          className="absolute top-3 right-3 w-8 h-8 rounded-full grid place-items-center text-muted hover:bg-paper transition"
        >
          <X size={16} />
        </button>

        {/* Hero */}
        <div className="bg-gradient-to-b from-signal/10 to-white px-5 pt-6 pb-3">
          <div className="flex items-center gap-3">
            <img
              src="/brand/skillconnect-icon.png?v=8"
              alt="SkillConnect"
              className="w-12 h-12 rounded-xl border border-rule bg-white object-contain p-1 shadow-sm"
            />
            <div>
              <div className="font-display font-bold text-base leading-tight">Get the SkillConnect App</div>
              <div className="text-xs text-muted mt-0.5">Skills → Careers in your pocket</div>
            </div>
          </div>
          <p className="text-sm text-ink/80 mt-3">
            Apply faster, get instant interview pings and track your placements offline.
          </p>
        </div>

        {/* Stats */}
        <div className="px-5 pb-3 flex items-center gap-4 text-xs">
          <span className="inline-flex items-center gap-1 text-amber-700">
            <Star size={13} className="fill-amber-500 stroke-amber-500" /> <b>4.7</b> across stores
          </span>
          <span className="inline-flex items-center gap-1 text-muted">
            <Award size={13} /> Verified employers only
          </span>
        </div>

        {/* CTAs */}
        <div className="px-5 pb-5 pt-2 space-y-2">
          {iosHint ? (
            <div className="text-xs text-muted bg-paper border border-rule rounded-lg p-3 leading-relaxed">
              On iPhone: tap the <b>Share</b> icon below, then choose <b>Add to Home Screen</b> to install SkillConnect.
            </div>
          ) : (
            <button
              type="button"
              onClick={install}
              data-testid="install-banner-install"
              className="btn-primary w-full justify-center text-base"
            >
              <Smartphone size={16} /> Install the app
            </button>
          )}
          <button
            type="button"
            onClick={dismiss}
            data-testid="install-banner-continue"
            className="block w-full text-center text-sm text-signal font-semibold hover:underline py-1.5"
          >
            Continue in browser
          </button>
        </div>
      </div>
    </div>
  );
}
